import { useQuery } from "@tanstack/react-query";
import { fetchSources, fmtRelative } from "../lib/api";
import { useState } from "react";
import { ExternalLink } from "lucide-react";
import HowItWorks from "../components/HowItWorks";

export default function SourcesPage() {
  const [sort, setSort] = useState("credibility");
  const { data: sources = [] } = useQuery({
    queryKey: ["sources", sort],
    queryFn: () => fetchSources({ sort, limit: 300 }),
    refetchInterval: 15000,
  });

  return (
    <div data-testid="sources-page" className="p-4 space-y-3">
      <div className="flex items-end justify-between flex-wrap gap-2">
        <div>
          <h1 className="font-mono text-2xl tracking-tight">SOURCE_LEDGER</h1>
          <p className="text-zinc-500 text-[12px] mt-1 max-w-2xl leading-relaxed">
            The full library of URLs we&apos;ve cited — across topics, innovation,
            and fun-facts. Sources are scored by domain credibility, recency,
            and how many ideas they back. Use this page to spot which
            publishers carry the most signal and audit the engine&apos;s rigor.
          </p>
        </div>
        <div className="flex gap-1">
          {[
            { v: "credibility", l: "CREDIBILITY" },
            { v: "repeats", l: "REPEATS" },
            { v: "recent", l: "RECENT" },
          ].map((o) => (
            <button
              key={o.v}
              data-testid={`sources-sort-${o.v}`}
              onClick={() => setSort(o.v)}
              className={
                "h-7 px-2 text-[10px] font-mono uppercase tracking-wider border " +
                (sort === o.v
                  ? "border-white bg-white text-black"
                  : "border-[#27272A] text-zinc-400 hover:text-white hover:bg-[#141414]")
              }
            >
              {o.l}
            </button>
          ))}
        </div>
      </div>

      <HowItWorks
        id="sources"
        tag="CREDIBILITY × REUSE × RECENCY"
        headline="Every URL we cite gets a credibility score, a repeat count, and a verification timestamp. Top sources rise; weak ones get downweighted."
        steps={[
          { accent: "emerald", label: "CRED column", body: "0–100 trust score by publisher tier (Reuters/Nature/NIH = top tier; aggregators rank lower)." },
          { accent: "amber", label: "REPEATS column", body: "How many ideas across the platform cite this URL. High repeats = recurring signal source." },
          { accent: "purple", label: "VERIFIED status", body: "Live HEAD/GET probe result — green when the URL still resolves, red if broken." },
          { accent: "sky", label: "SORT tabs", body: "Switch the view between CREDIBILITY (default), REPEATS (most reused), or RECENT (newest discovered)." },
          { accent: "rose", label: "URL link", body: "Click the external icon to open the source in a new tab — verify the citation yourself." },
        ]}
      />

      <section className="border border-[#27272A] bg-[#0A0A0A] overflow-x-auto">
        <table className="w-full text-[11px] font-mono min-w-[900px]">
          <thead className="text-zinc-500 bg-[#0E0E0E] sticky top-0">
            <tr className="border-b border-[#27272A]">
              <th className="text-left p-2 uppercase tracking-wider">DOMAIN</th>
              <th className="text-left p-2 uppercase tracking-wider">PUBLISHER</th>
              <th className="text-right p-2 uppercase tracking-wider">CRED</th>
              <th className="text-right p-2 uppercase tracking-wider">REPEATS</th>
              <th className="text-left p-2 uppercase tracking-wider">VERIFIED</th>
              <th className="text-left p-2 uppercase tracking-wider">FIRST_SEEN</th>
              <th className="text-left p-2 uppercase tracking-wider">URL</th>
            </tr>
          </thead>
          <tbody>
            {sources.map((s) => (
              <tr
                key={s.id}
                data-testid={`source-row-${s.id}`}
                className="border-b border-[#161616] hover:bg-[#0E0E0E]"
              >
                <td className="p-2 text-white">{s.domain}</td>
                <td className="p-2 text-zinc-400 truncate max-w-[200px]">
                  {s.publisher || "—"}
                </td>
                <td className="p-2 text-right tabnum">
                  {Number(s.credibility_score).toFixed(0)}
                </td>
                <td className="p-2 text-right tabnum">{s.repeat_count}</td>
                <td className="p-2 uppercase">
                  <span
                    className="dot"
                    style={{ background: s.verified ? "#10B981" : "#EF4444" }}
                  />
                  {s.verified ? "OK" : "BROKEN"}
                </td>
                <td className="p-2 text-zinc-500">{fmtRelative(s.first_seen_at)}</td>
                <td className="p-2">
                  <a
                    href={s.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="src-link inline-flex items-center gap-1 max-w-[280px] truncate"
                  >
                    {s.url}
                    <ExternalLink size={10} />
                  </a>
                </td>
              </tr>
            ))}
            {sources.length === 0 && (
              <tr>
                <td colSpan={7} className="p-6 text-center text-zinc-500">
                  NO_SOURCES_YET
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </section>
    </div>
  );
}
